import type {
  CustomFieldType,
  CustomFieldVisibilityType,
  RelatedObjectType,
} from './custom-fields.constants';
import type { HiddenFieldsByModule } from 'src/app/core/store/field-visibility/field-visibility.types';

export interface IUserModule {
    module: string;
    own_data: boolean;
}

export interface IUser {
    id: number;
    name: string;
    first_name: string;
    last_name: string;
    email: string;
    phone?: string;
    image: string;
    enabled: boolean;
    is_deleted?: boolean;
    deleted_at?: string | null;
    roles: IRole[];
    role_ids: number[];
    lang?: string;
    last_activity?: string;
    receive_emails?: boolean;
    is_super_admin?: boolean;
    modules?: IUserModule[];
}

export interface IUserCreate {
    name: string;
    email: string;
    enabled: boolean;
    role_ids: number[];
}

export interface IUserUpdate extends Partial<IUserCreate> {
    id: number;
}

export interface IUserFormPayload {
    name: string;
    email: string;
    roleId: number;
}

export interface IRole {
    id: number;
    name: string;
    description: string;
    permissions: IPermission[];
    permission_ids: number[];
    user_ids: number[];
    users: IUser[];
    edited_by?: {
        id: number;
        name: string;
    };
    last_edited?: string;
    is_protected?: boolean;
}

export interface IAddRole {
    name: string;
}

export interface IUpdateRole {
    name?: string;
    permission_ids?: number[];
    hidden_field_ids?: HiddenFieldsByModule;
}

/**
 * A member as the `/v1/teams` endpoints return it — a strict subset of `IUser`.
 * The backend serialises only these three fields, so typing it as a full `IUser`
 * would let callers read `email` or `roles` and get `undefined` with no error.
 */
export type ITeamMember = Pick<IUser, 'id' | 'name' | 'image'>;

export interface ITeam {
    id: number;
    name: string;
    description?: string;
    member_ids: number[];
    members: ITeamMember[];
    member_count: number;
    /** Pre-formatted `"YYYY-MM-DD HH:mm:ss"`, NOT an ISO datetime. Parse explicitly before reformatting. */
    last_edited: string;
}

/**
 * A team as `GET /v1/teams/options` returns it: id and name, nothing else.
 *
 * Deliberately NOT `ITeam`. `/v1/teams` is gated behind `access:settings_team` and carries
 * members and counts; the options route is readable by any order/inbox user and carries
 * neither. Sharing one type would invite a picker to read `members` and get `undefined`.
 */
export type ITeamOption = Pick<ITeam, 'id' | 'name'>;

export interface IAddTeam {
    name: string;
    description?: string;
    member_ids?: number[];
}

export interface IUpdateTeam {
    name?: string;
    description?: string;
    /** Sent verbatim when present — `[]` clears every member, it is not "no change". */
    member_ids?: number[];
}

/** The only three columns `GET /v1/teams` accepts. `write_date` is rejected with a 400. */
export const TEAM_SORT_FIELDS = ['name', 'member_count', 'last_edited'] as const;
export type TeamSortField = typeof TEAM_SORT_FIELDS[number];
/** The API is case-sensitive here: `ASC` returns 400. */
export type TeamSortMode = 'asc' | 'desc';

export interface ITeamSort {
    sort_by: TeamSortField;
    sort_mode: TeamSortMode;
}

export type RoleFieldVisibilityLevel = 'visible' | 'hidden';

export interface IRoleFieldVisibilityOption {
    key: string;
    label: string;
    objectType: string;
    visibility: RoleFieldVisibilityLevel;
}

export interface IPermission {
    id: number;
    name: string;
    description: string;
    module_id?: number | null;
}

export interface IModule {
    id: number;
    name: string;
    permissions: IPermission[]
}

export interface INotificationType {
    id: number;
    name: string;
}

export interface INotification {
    id: number;
    users: IUser[];
    types: INotificationType[];
}

export interface INotificationTypeUpdate {
    type_ids: number[];
    user_ids: number[];
}

export interface ICustomField {
    id: number;
    technical_name: string;
    object_type: RelatedObjectType;
    field_type: CustomFieldType;
    label: { [key: string]: string };
    help_text: { [key: string]: string };
    allowed_values: string[];
    visibility: CustomFieldVisibilityType[];
    is_required: boolean;
    erp_mapping_key: string | null;
}

export interface IApplicabilityField {
    id: number;
    technical_name: string;
    label: string;
    field_type: string;
    value_options: string[];
}

// interface for the custom field form
export interface ICustomFieldForm {
    object_type: RelatedObjectType;
    field_type: CustomFieldType;
    label: { [key: string]: string };
    help_text: { [key: string]: string };
    allowed_values: string[];
    visibility: CustomFieldVisibilityType[];
    is_required: boolean;
    erp_mapping_key: string | null;
}


export interface IObjectCustomFields {
    values: {
      [key: string]: string | string[] | number | boolean;
    };
    definitions: ICustomField[];
  }

export interface IObjectCustomFieldsParams {
    object_type: RelatedObjectType;
    record_id: number;
    visibility?: CustomFieldVisibilityType[];
    include_definitions?: boolean;
}

// Smart Inbox settings (mailboxes)
export type MailboxProvider = 'gmail' | 'o365' | 'exchange';
export type MailboxType = 'personal' | 'team';
export type ConnectionStatus = 'connected' | 'disconnected';
export type SyncStatus = 'idle' | 'syncing' | 'error' | 'paused';

export interface IMailboxSettings {
  id: number;
  name: string;
  email: string;
  provider: MailboxProvider;
  mailbox_type: MailboxType;
  mailbox_owner_id: number | null;
  mailbox_owner_name: string | null;
  can_view_ids: number[];
  can_send_ids: number[];
  connection_status: ConnectionStatus;
  sync_status: SyncStatus;
  last_sync_at: string | null;
  last_sync_error: string | null;
  active: boolean;
  unread_count?: number;
  users_with_access?: { user_id: number; name: string }[];
}

export interface IMailboxSettingsUpdate {
  name?: string;
  can_view?: number[];
  can_send?: number[];
}

/**
 * Company-level default price source for orders.
 * - `volta`     — use System (Volta) prices (default).
 * - `extracted` — use AI-extracted prices from customer documents.
 * - `none`      — create orders without pre-filled prices.
 */
export type OrderPriceSource = 'volta' | 'extracted' | 'none';

export interface IOrdersSettings {
  default_price_source: OrderPriceSource;
  /**
   * Only applicable when `default_price_source === 'none'`. When true, pricing
   * and metrics columns are hidden BY DEFAULT on the orders list, order details
   * and side-by-side views (users can still re-enable them per-view).
   */
  hide_pricing_columns: boolean;
}

// Smart Replies settings (global AI reply settings — WB-6371 scope v3)
// Two presentation rules only. The sign-off block comes from the mailbox
// signature and stock handling is hardcoded by Alta, so neither is exposed here.
export type SmartRepliesPricingSource = 'any' | 'kb' | 'db' | 'placeholder';
export type SmartRepliesProductFormat = 'bullets' | 'table';

export interface ISmartRepliesSettings {
  pricing_source: SmartRepliesPricingSource;
  product_format: SmartRepliesProductFormat;
}

export interface ISplittingRule {
  id: number;
  name: string;
  active: boolean;
  sequence: number;
  condition_type: 'brand' | 'category' | 'product_label' | 'product_custom_field';
  condition_custom_field_id: number | null;
  condition_custom_field_name: string | null;
  split_mode: 'all' | 'specific';
  split_values: string[];
  applicability_type: 'all' | 'customer' | 'customer_group' | 'customer_custom_field';
  applicability_partner_ids: { id: number; name: string }[];
  applicability_company_ids: { id: number; name: string }[];
  applicability_location_group_ids: { id: number; name: string }[];
  applicability_custom_field_id: number | null;
  applicability_custom_field_name: string | null;
  applicability_custom_field_value: string | null;
  log_count: number;
}

export interface ISplittingRuleCreate {
  name: string;
  active: boolean;
  sequence?: number;
  condition_type: 'brand' | 'category' | 'product_label' | 'product_custom_field';
  condition_custom_field_id?: number | null;
  split_mode: 'all' | 'specific';
  split_values: string[];
  applicability_type: 'all' | 'customer' | 'customer_group' | 'customer_custom_field';
  applicability_partner_ids?: number[];
  applicability_company_ids?: number[];
  applicability_location_group_ids?: number[];
  applicability_custom_field_id?: number | null;
  applicability_custom_field_value?: string | null;
}